import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { signalRService } from './services/signalr';
import { ErrorBoundary } from './components/ErrorBoundary';

// Auth/public pages stay in main bundle for fast first load
import { Home } from './pages/Home';
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';

// Heavy pages code-split into separate chunks
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const RetroBoard = lazy(() => import('./pages/RetroBoard').then(m => ({ default: m.RetroBoard })));
const Join = lazy(() => import('./pages/Join').then(m => ({ default: m.Join })));
const PokerSession = lazy(() => import('./pages/PokerSession').then(m => ({ default: m.PokerSession })));
const CreatePoker = lazy(() => import('./pages/CreatePoker').then(m => ({ default: m.CreatePoker })));
const JoinPoker = lazy(() => import('./pages/JoinPoker').then(m => ({ default: m.JoinPoker })));
const Profile = lazy(() => import('./pages/Profile').then(m => ({ default: m.Profile })));

const PageLoader: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
  </div>
);


// Allows authenticated users OR guests for board/poker access
const ProtectedRoute: React.FC<{
  children: React.ReactNode;
  allowGuest?: boolean;
  guestRetroId?: string;
  guestPokerSessionId?: string;
}> = ({
  children,
  allowGuest = false,
  guestRetroId,
  guestPokerSessionId
}) => {
  const { isAuthenticated, isGuest, guest, isLoading } = useAuth();

  if (isLoading) {
    return <PageLoader />;
  }

  if (isAuthenticated) {
    return <>{children}</>;
  }

  if (isGuest && allowGuest && guest) {
    if (guestRetroId && 'retrospectiveId' in guest && guest.retrospectiveId === guestRetroId) {
      return <>{children}</>;
    }
    if (guestPokerSessionId && 'pokerSessionId' in guest && guest.pokerSessionId === guestPokerSessionId) {
      return <>{children}</>;
    }
    if (!guestRetroId && !guestPokerSessionId) {
      return <>{children}</>;
    }
  }

  return <Navigate to="/login" />;
};

const RetroBoardWrapper: React.FC = () => {
  const { retroId } = useParams<{ retroId: string }>();
  return (
    <ProtectedRoute allowGuest={true} guestRetroId={retroId}>
      <RetroBoard />
    </ProtectedRoute>
  );
};

const PokerSessionWrapper: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  return (
    <ProtectedRoute allowGuest={true} guestPokerSessionId={sessionId}>
      <PokerSession />
    </ProtectedRoute>
  );
};

const SignalRProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isGuest, isLoading } = useAuth();

  useEffect(() => {
    // Wait for auth state to load — calling stop() before load would break SignalR
    if (isLoading) return;

    if (isAuthenticated || isGuest) {
      signalRService.start();
    } else {
      signalRService.stop();
    }
  }, [isAuthenticated, isGuest, isLoading]);

  return <>{children}</>;
};

const AppContent: React.FC = () => {
  return (
    <SignalRProvider>
      <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        {/* Token-only invite, symmetric with /poker/join/:inviteToken. Legacy /retro/join and /join
            routes are kept so previously-shared links keep working. */}
        <Route path="/retrospective/join/:inviteToken" element={<Join />} />
        <Route path="/retro/join/:inviteToken" element={<Join />} />
        <Route path="/join/:inviteToken" element={<Join />} />
        <Route path="/join/:retroId/:inviteToken" element={<Join />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route path="/retro/:retroId" element={<RetroBoardWrapper />} />

        <Route
          path="/poker/create"
          element={
            <ProtectedRoute>
              <CreatePoker />
            </ProtectedRoute>
          }
        />
        <Route path="/poker/join/:inviteToken" element={<JoinPoker />} />
        <Route path="/poker/:sessionId" element={<PokerSessionWrapper />} />

        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      </Suspense>
    </SignalRProvider>
  );
};

const ThemeWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isGuest, isLoading } = useAuth();
  const location = useLocation();

  const alwaysLightPage = ['/', '/login', '/register', '/forgot-password', '/reset-password'].includes(location.pathname)
    || location.pathname.includes('/join');
  // Don't force light while isLoading — prevents clearing saved dark mode preference
  const forceLight = alwaysLightPage || (!isLoading && !isAuthenticated && !isGuest);

  return (
    <ThemeProvider forceLight={forceLight}>
      {children}
    </ThemeProvider>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <ThemeWrapper>
            <AppContent />
          </ThemeWrapper>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
